import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Button } from "./ui/button";
import { ArrowRight, MapPin, Calendar, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "motion/react";
import mixedFarmImage from "../assets/mixed-farm.png";
import dripIrrigationImage from "../assets/drip-irrigation.png";
import polyhouseImage from "../assets/commercial-polyhouse.png";
import droneImage from "../assets/drone-image.png";
import soilTestingImage from "../assets/soil-testing.png";
import modernEquipmentImage from "../assets/modern-equipment.png";
import { useLanguage } from "../context/LanguageContext";
import { useRef } from "react";

export function ProjectsShowcase() {
  const { t } = useLanguage();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  
  const projects = [
    {
      image: mixedFarmImage,
      title: t('projects.list[0].title'),
      location: t('projects.list[0].location'),
      date: "2024",
      type: t('projects.list[0].type')
    },
    {
      image: dripIrrigationImage,
      title: t('projects.list[1].title'),
      location: t('projects.list[1].location'),
      date: "2024",
      type: t('projects.list[1].type')
    },
    {
      image: polyhouseImage,
      title: t('projects.list[2].title'),
      location: t('projects.list[2].location'),
      date: "2024",
      type: t('projects.list[2].type')
    },
    {
      image: droneImage,
      title: t('projects.list[3].title'),
      location: t('projects.list[3].location'),
      date: "2024",
      type: t('projects.list[3].type')
    },
    {
      image: soilTestingImage,
      title: t('projects.list[4].title'),
      location: t('projects.list[4].location'),
      date: "2024",
      type: t('projects.list[4].type')
    },
    {
      image: modernEquipmentImage,
      title: t('projects.list[5].title'),
      location: t('projects.list[5].location'),
      date: "2024",
      type: t('projects.list[5].type')
    }
  ];

  return (
    <section id="projects" className="py-32 px-6 lg:px-20 bg-gradient-to-b from-white via-[#DAF1DE]/30 to-white relative overflow-hidden">
      {/* Floating Particles */}
      {[...Array(10)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-3 h-3 bg-gradient-to-br from-[#8EB69B] to-[#235347] rounded-full opacity-20"
          animate={{
            y: [Math.random() * 100, Math.random() * -100],
            x: [Math.random() * 100, Math.random() * -100],
            scale: [1, 1.5, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: Math.random() * 5 + 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`
          }}
        />
      ))}
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block bg-gradient-to-r from-[#8EB69B]/20 to-[#235347]/20 px-6 py-3 rounded-full border border-[#235347]/20 mb-6 backdrop-blur-sm"
          >
            <p className="text-[#163832] tracking-wider uppercase text-sm">Our Projects</p>
          </motion.div>
          
          <motion.h2 
            className="text-5xl lg:text-6xl text-[#051F20] mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {t('projects.title')}
          </motion.h2>
          
          <motion.p 
            className="text-gray-600 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            {t('projects.subtitle')}
          </motion.p>
        </div>

        {/* Horizontal Scrolling Projects with Controls */}
        <div className="relative mb-16">
          {/* Scroll Left Button */}
          <motion.button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-md p-3 rounded-full shadow-2xl border border-[#235347]/20 hover:bg-[#235347] hover:text-white transition-all duration-300"
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft className="h-5 w-5" />
          </motion.button>

          {/* Scroll Right Button */}
          <motion.button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-md p-3 rounded-full shadow-2xl border border-[#235347]/20 hover:bg-[#235347] hover:text-white transition-all duration-300"
            whileHover={{ scale: 1.1, x: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronRight className="h-5 w-5" />
          </motion.button>

          {/* Horizontal Scroll Container */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-2 pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 80 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  delay: index * 0.1,
                  duration: 0.6,
                }}
                whileHover={{ 
                  y: -10,
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
                className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 flex-shrink-0 w-80"
              >
                <div className="relative h-96 overflow-hidden rounded-2xl">
                  {/* Image */}
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                    className="h-full"
                  >
                    <ImageWithFallback 
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                  
                  {/* Gradient Overlays */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#051F20] via-[#051F20]/50 to-transparent"></div>
                  
                  {/* Hover Overlay with Blur */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-[#235347]/95 via-[#235347]/60 to-transparent backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.4 }}
                  />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <motion.div 
                      className="bg-[#8EB69B]/90 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full w-fit mb-3"
                      initial={{ x: -20, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      {project.type}
                    </motion.div>
                    
                    <motion.h3 
                      className="text-white text-xl mb-2"
                      whileHover={{ x: 5, color: '#8EB69B' }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {project.title}
                    </motion.h3>
                    
                    <div className="flex items-center gap-3 text-white/80 text-sm mb-3">
                      <motion.div 
                        className="flex items-center gap-1"
                        whileHover={{ scale: 1.1 }}
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{project.location}</span>
                      </motion.div>
                      <motion.div 
                        className="flex items-center gap-1"
                        whileHover={{ scale: 1.1 }}
                      >
                        <Calendar className="h-3.5 w-3.5" />
                        <span>{project.date}</span>
                      </motion.div>
                    </div>
                    
                    {/* View Details Button */}
                    <motion.button 
                      className="flex items-center gap-2 text-white hover:text-[#8EB69B] transition-colors bg-white/10 backdrop-blur-md px-4 py-2 rounded-full w-fit border border-white/20"
                      initial={{ y: 20, opacity: 0 }}
                      whileHover={{ y: 0, opacity: 1, scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      <span className="text-sm">{t('projects.viewProject')}</span>
                      <motion.div
                        animate={{ x: [0, 3, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <ArrowRight className="h-3.5 w-3.5" />
                      </motion.div>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button className="bg-gradient-to-r from-[#235347] to-[#8EB69B] hover:from-[#163832] hover:to-[#235347] text-white px-12 py-7 text-lg shadow-2xl hover:shadow-[#8EB69B]/30 transition-all duration-300 rounded-2xl group relative overflow-hidden">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              />
              <span className="relative z-10 flex items-center gap-2">
                {t('common.viewAll')} Projects
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight className="h-5 w-5" />
                </motion.div>
              </span>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}